/**
 * useTokens hook
 *
 * TanStack Query infinite query hook for fetching paginated token lists.
 * Supports search, category filter, sort, order, and verification filter.
 * All filter state is passed from URL search params (managed by DiscoverPage).
 *
 * Story 3.4 — Discover Page UI
 */

import type { PaginatedTokenList } from "@repo/shared-types";
import { useInfiniteQuery } from "@tanstack/react-query";

import { type TokenListParams, tokensApi } from "../../lib/api";
import { tokenKeys } from "./query-keys";

export type TokenFilters = {
	search?: string;
	category?: string;
	sort?: string;
	order?: string;
	verification?: string;
};

function filtersToKey(filters: TokenFilters): string {
	return JSON.stringify(filters);
}

const NO_PAGE_PARAM: string | undefined = undefined;

export function useTokens(filters: TokenFilters) {
	return useInfiniteQuery<PaginatedTokenList, Error>({
		queryKey: tokenKeys.list(filtersToKey(filters)),
		queryFn: async ({ pageParam }) => {
			const params: TokenListParams = {
				...filters,
				limit: 20,
			};
			if (typeof pageParam === "string") {
				params.cursor = pageParam;
			}
			const response = await tokensApi.list(params);
			return response.data;
		},
		initialPageParam: NO_PAGE_PARAM,
		getNextPageParam: (lastPage) =>
			lastPage.pagination.hasMore ? (lastPage.pagination.nextCursor ?? undefined) : undefined,
		staleTime: 30_000,
	});
}
